import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Home, Lock, Maximize, X } from "lucide-react";
import { kioskLockCode, setKioskLock, fullscreenSupported, isFullscreen, enterFullscreen, exitFullscreen } from "@/lib/kioskLock";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const KIOSK_CHILD = /^\/(checkin|tracker-signup|prayer)\//;
let exiting = false;
const T = {
  en: { back: "Back to kiosk home", locked: "Kiosk locked", resume: "Tap to resume full screen", exit: "Exit kiosk", pwTitle: "Admin password", pwHint: "Enter a church admin's login password to exit kiosk mode.", unlock: "Unlock", cancel: "Cancel" },
  es: { back: "Volver al inicio", locked: "Kiosco bloqueado", resume: "Toca para volver a pantalla completa", exit: "Salir del kiosco", pwTitle: "Contraseña de administrador", pwHint: "Ingresa la contraseña de un administrador para salir del modo kiosco.", unlock: "Desbloquear", cancel: "Cancelar" },
};
const langOf = (search) => {
  const p = new URLSearchParams(search).get("lang");
  if (p) return p === "es" ? "es" : "en";
  try { return localStorage.getItem("kiosk-lang") === "es" ? "es" : "en"; } catch { return "en"; }
};

function UnlockForm({ code, t, onCancel, onUnlocked }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await api.post(`/public/kiosk/${code}/unlock`, { password: pw }); onUnlocked(); }
    catch (er) { setErr(er?.response?.data?.detail || "Couldn't unlock."); setPw(""); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="w-full max-w-sm rounded-2xl p-6 space-y-4" style={{ background: "var(--bg-alt)", border: "1.5px solid var(--gold)" }} data-testid="kiosk-unlock-form">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>{t.pwTitle}</h3>
        <button type="button" onClick={onCancel} className="p-2" style={{ color: "var(--muted)" }} aria-label={t.cancel} data-testid="kiosk-unlock-cancel"><X size={18} /></button>
      </div>
      <p className="text-xs" style={{ color: "var(--muted)" }}>{t.pwHint}</p>
      <input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} className="w-full px-4 py-3 text-base" autoComplete="current-password" data-testid="kiosk-unlock-password" />
      {err && <p className="text-xs" style={{ color: "var(--terracotta)" }} data-testid="kiosk-unlock-error">{err}</p>}
      <button type="submit" disabled={busy || !pw} className="btn-primary w-full justify-center" data-testid="kiosk-unlock-submit">{busy ? "…" : t.unlock}</button>
    </form>
  );
}

// Global guard: while a tab is in kiosk mode, keep it on kiosk pages, trap back, require full screen.
export default function KioskGuard() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const homeMatch = pathname.match(/^\/kiosk\/([^/]+)/);
  if (homeMatch && !exiting && kioskLockCode() !== homeMatch[1]) setKioskLock(homeMatch[1]);
  const code = kioskLockCode();
  const isHome = !!homeMatch;
  const [fs, setFs] = useState(isFullscreen());
  const [unlocking, setUnlocking] = useState(false);
  const [, setLangTick] = useState(0);
  const t = T[langOf(search)];

  useEffect(() => {
    const onLang = () => setLangTick((n) => n + 1);
    window.addEventListener("kiosk-lang", onLang);
    return () => window.removeEventListener("kiosk-lang", onLang);
  }, []);

  useEffect(() => {
    const badge = document.getElementById("brand-badge");
    if (!code || !badge) return;
    const prev = badge.style.display;
    badge.style.display = "none";
    return () => { badge.style.display = prev; };
  }, [code]);
  const home = `/kiosk/${code}`;

  useEffect(() => {
    if (code && !isHome && !KIOSK_CHILD.test(pathname)) navigate(home, { replace: true });
  }, [code, isHome, pathname, home, navigate]);

  useEffect(() => {
    if (!code) return;
    const onFs = () => setFs(isFullscreen());
    const onPop = () => { window.history.pushState(null, "", window.location.href); if (!isHome) navigate(home, { replace: true }); };
    const onUnload = (e) => { if (!kioskLockCode()) return; e.preventDefault(); e.returnValue = ""; };
    window.history.pushState(null, "", window.location.href);
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    window.addEventListener("popstate", onPop);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [code, isHome, home, navigate]);

  const unlocked = useCallback(async () => {
    exiting = true;
    setKioskLock(null);
    await exitFullscreen();
    window.location.replace("/kiosk-launch");
  }, []);

  if (!code || (!isHome && !KIOSK_CHILD.test(pathname))) return null;
  const blocked = fullscreenSupported() && !fs;

  return (
    <>
      {!isHome && (
        <button onClick={() => navigate(home, { replace: true })} className="fixed top-4 left-4 z-[60] inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium shadow-lg" style={{ background: "var(--gold)", color: "var(--bg-base)" }} data-testid="kiosk-back-home-btn">
          <Home size={18} /> {t.back}
        </button>
      )}
      {isHome && !blocked && (
        <button onClick={() => setUnlocking(true)} className="fixed bottom-4 right-4 z-[60] inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[0.65rem] uppercase tracking-[0.18em] opacity-60 hover:opacity-100" style={{ border: "1px solid var(--border-strong)", color: "var(--muted)", background: "var(--bg-base)" }} data-testid="kiosk-exit-btn">
          <Lock size={12} /> {t.exit}
        </button>
      )}
      {(blocked || unlocking) && (
        <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-6 px-6" style={{ background: "rgba(10,27,51,0.96)" }} data-testid="kiosk-lock-overlay">
          {unlocking ? (
            <UnlockForm code={code} t={t} onCancel={() => setUnlocking(false)} onUnlocked={unlocked} />
          ) : (
            <>
              <Lock size={40} style={{ color: "var(--gold)" }} />
              <h2 className="font-serif-display text-4xl text-center" style={{ color: "var(--ink)" }}>{t.locked}</h2>
              <button onClick={enterFullscreen} className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-medium" style={{ background: "var(--gold)", color: "var(--bg-base)" }} data-testid="kiosk-resume-fullscreen-btn">
                <Maximize size={18} /> {t.resume}
              </button>
              <button onClick={() => setUnlocking(true)} className="text-xs uppercase tracking-[0.2em] underline" style={{ color: "var(--muted)" }} data-testid="kiosk-overlay-exit-btn">{t.exit}</button>
            </>
          )}
        </div>
      )}
    </>
  );
}
